import { useEffect, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ProtectContext } from "../../context/protect_context";
import { IMAGE_BASE_URL } from "../../utils/image_url";

const NewsDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data } = useContext(ProtectContext);
  
  // Get news from state (for backward compatibility) or from context by ID
  const newsFromState = location.state?.advice || location.state?.news;
  const newsFromContext = id 
    ? data?.advices?.find((advice) => advice.id === parseInt(id))
    : null;
  
  // If newsFromState exists, ensure it has videoUrl from context if missing
  const news = newsFromState 
    ? {
        id: newsFromState.id,
        title: newsFromState.title,
        image: newsFromState.image 
          ? (newsFromState.image.startsWith('http') ? newsFromState.image : `${IMAGE_BASE_URL}${newsFromState.image}`)
          : null,
        videoUrl: newsFromState.videoUrl || newsFromContext?.videoUrl || null,
        desc: newsFromState.desc || null,
      }
    : (newsFromContext ? {
        id: newsFromContext.id,
        title: newsFromContext.title,
        image: newsFromContext.image ? `${IMAGE_BASE_URL}${newsFromContext.image}` : null,
        videoUrl: newsFromContext.videoUrl || null,
        desc: newsFromContext.desc || null,
      } : null);
  
  // Extract YouTube video ID from URL
  const extractVideoId = (url: string): string | null => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };
  
  // Get embed URL from video URL
  const getEmbedUrl = (url: string): string | null => {
    const videoId = extractVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!news) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center space-y-6 px-4">
          <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-red-100 to-orange-100">
            <svg
              className="h-10 w-10 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 lg:text-4xl">
            Зөвлөмж олдсонгүй
          </h1>
          <p className="text-lg text-slate-600">
            Уучлаарай, таны хайж буй зөвлөмж олдсонгүй
          </p>
          <button
            onClick={() => navigate("/")}
            className="group inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-red-600 via-red-500 to-orange-500 px-8 py-4 text-base font-bold text-white shadow-lg shadow-red-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-red-500/60"
          >
            Нүүр хуудас руу буцах
            <svg
              className="h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Modern Header Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 lg:py-16">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-gradient-to-br from-red-500/20 to-orange-500/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 blur-3xl" />
        </div>

        <div className="relative mx-auto w-full max-w-5xl px-4 lg:px-8">
          {/* Back button */}
          <button
            onClick={() => navigate("/")}
            className="group mb-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 py-3 text-sm font-semibold text-white backdrop-blur-md transition-all duration-300 hover:bg-white/20 hover:scale-105"
          >
            <svg
              className="h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Буцах
          </button>

          {/* Title and description */}
          <div className="space-y-6 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2 backdrop-blur-md">
              <div className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
              <span className="text-xs font-bold uppercase tracking-wider text-white">
                Зөвлөмж
              </span>
            </div>
            <h1 className="text-4xl font-extrabold leading-tight text-white drop-shadow-2xl lg:text-5xl xl:text-6xl">
              {news.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Video/Image Section - Premium Design */}
      {(news.videoUrl && getEmbedUrl(news.videoUrl)) || news.image ? (
        <section className="relative -mt-8 px-4 lg:px-8 lg:-mt-12">
          <div className="mx-auto w-full max-w-6xl">
            <div className="group relative">
              {/* Glow effect behind video/image */}
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-red-500/20 via-orange-500/20 to-red-500/20 opacity-0 blur-2xl transition-opacity duration-700 group-hover:opacity-100" />

              {/* Video/Image container with premium styling */}
              <div className="relative overflow-hidden rounded-3xl bg-slate-900 shadow-2xl transition-all duration-700 group-hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]">
                {/* Video/Image wrapper */}
                <div className="relative w-full overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 to-black pt-[56.25%]">
                  {news.videoUrl && getEmbedUrl(news.videoUrl) ? (
                    <iframe
                      className="absolute inset-0 h-full w-full transition-transform duration-700 group-hover:scale-[1.02]"
                      src={getEmbedUrl(news.videoUrl) || ""}
                      title={news.title || "Мэдээний видео"}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  ) : news.image ? (
                    <img
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                      src={news.image}
                      alt={news.title || "Зөвлөмжийн зураг"}
                    />
                  ) : null}
                </div>

                {/* Decorative overlay on hover */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                {/* Video controls hint (only for video) */}
                {news.videoUrl && getEmbedUrl(news.videoUrl) && (
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between opacity-0 transition-opacity duration-500 group-hover:opacity-100 lg:bottom-6 lg:left-6 lg:right-6">
                    <div className="flex items-center gap-3 rounded-full border border-white/20 bg-black/40 px-4 py-2 backdrop-blur-md">
                      <svg
                        className="h-5 w-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5.14v13.72L19 12 8 5.14z" />
                      </svg>
                      <span className="text-xs font-semibold text-white">
                        YouTube дээр үзэх
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Decorative corner elements */}
              <div className="absolute -top-6 -right-6 hidden h-32 w-32 rounded-full bg-gradient-to-br from-red-500/10 to-orange-500/10 blur-2xl lg:block" />
              <div className="absolute -bottom-6 -left-6 hidden h-32 w-32 rounded-full bg-gradient-to-br from-blue-500/10 to-purple-500/10 blur-2xl lg:block" />
            </div>
          </div>
        </section>
      ) : null}

      {/* Additional info section */}
      <section className="py-12 lg:py-16">
        <div className="mx-auto w-full max-w-5xl px-4 lg:px-8">
          <div className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-xl transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl lg:p-12">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/0 to-orange-50/0 transition-all duration-500 group-hover:from-red-50/50 group-hover:to-orange-50/50" />

            <div className="relative z-10 space-y-6">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 shadow-lg">
                  <svg
                    className="h-7 w-7 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 lg:text-2xl">
                    Зөвлөмжийн дэлгэрэнгүй
                  </h3>
                  <p className="text-sm text-slate-600">
                    Гамшигаас урьдчилан сэргийлэх зөвлөмж
                  </p>
                </div>
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

              <div className="space-y-4">
                {news.desc && (
                  <p className="mx-auto max-w-3xl text-lg leading-relaxed text-slate-600 lg:text-xl">
                    {news.desc}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetailPage;
